/* eslint-disable import/order */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Alert, Form, Row, Col, Button, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { SelectController } from '../../../../app/components/SelectController';
import { Group, GroupService, User, UserService } from '../../../../api';
import { Pagination } from '../../../../app/components/Pagination/Pagination';
import { useSearchParamsState } from '../../../../app/hooks/search-params';
import 'react-range-slider-input/dist/style.css';
import styles from './UsersList.module.scss';
import { Card } from '../../../../app/components/Card';
import { BsPersonLock, BsPersonUp, BsTrash } from 'react-icons/bs';
import { userQueryKeys } from '../../../../app/query-keys-factories/user';
import { groupQueryKeys } from '../../../../app/query-keys-factories/groups';
import { GROUP_NAMES_MAPPING } from '../../../../app/constants/groups';
import { EditUserGroupsDialog } from './EditUserGroupsDialog';
import { toast } from 'react-toastify';
import { useCurrentUser } from '../../../../app/hooks/auth';
import PropTypes from 'prop-types';
import { handleApiError } from '../../../../app/utils/errorhandling';

const PAGE_SIZE = 20;

const EditUserGroupsButton = ({ user }: { user: User }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <Button
        variant="outline-primary"
        size="sm"
        className="shadow"
        title="Edit"
        onClick={() => setShow(true)}
      >
        <BsPersonLock />
      </Button>
      <EditUserGroupsDialog show={show} onClose={() => setShow(false)} user={user} />
    </>
  );
};

const DeactivateUserButton = ({ user }: { user: User }) => {
  const [show, setShow] = useState(false);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      await UserService.userDestroy({ id: user.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(userQueryKeys.all());
    },
  });

  const handleDeleteClick = async () => {
    try {
      await mutation.mutateAsync();
      toast.success('User deactivated successfully');
      setShow(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <>
      <Button
        variant="outline-danger"
        className="shadow"
        size="sm"
        title="Deactivate"
        onClick={() => setShow(true)}
      >
        <BsTrash />
      </Button>
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Deactivate User</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to deactivate this user?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => handleDeleteClick()}>
            Deactivate
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const ReactivateUserButton = ({ user }: { user: User }) => {
  const [show, setShow] = useState(false);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      await UserService.userReactivateUpdate({ id: user.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(userQueryKeys.all());
    },
  });

  const handleReactivateClick = async () => {
    try {
      await mutation.mutateAsync();
      toast.success('User reactivated successfully');
      setShow(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <>
      <Button variant="light" size="sm" title="Reactivate" onClick={() => setShow(true)}>
        <BsPersonUp />
      </Button>
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reactivate User</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to reactivate this user?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => handleReactivateClick()}>
            Reactivate
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// Add interfaces for props
interface ActionCellProps {
  row: {
    original: User;
  };
}

// Fix ActionCell component
const ActionCell = React.memo(({ row }: ActionCellProps) => {
  const { user } = useCurrentUser();

  if (user?.id === row.original.id) {
    return null;
  }

  return (
    <div className="d-flex justify-content-start gap-2">
      {row.original.is_active && <EditUserGroupsButton user={row.original} />}
      {row.original.is_active ? (
        <DeactivateUserButton user={row.original} />
      ) : (
        <ReactivateUserButton user={row.original} />
      )}
    </div>
  );
});

// Add both displayName and propTypes after the component definition
ActionCell.displayName = 'ActionCell';
(ActionCell as any).propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const UsersList = () => {
  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        header: 'Name',
        accessorFn: (row): string => `${row.first_name} ${row.last_name}`,
        size: 30,
        minSize: 0,
      },
      {
        header: 'Email',
        accessorKey: 'email',
        size: 25,
        minSize: 0,
      },
      {
        header: 'Permissions',
        accessorFn: (row): string =>
          row?.group_names?.map((name) => GROUP_NAMES_MAPPING[name]).join(', ') ?? '',
        cell: (props: TableCellProps) => <div className={styles.lineClamp}>{props.getValue()}</div>,
        size: 25,
        minSize: 0,
      },
      {
        header: 'Status',
        accessorFn: (row): string => (row?.is_active ? 'Active' : 'Inactive'),
        size: 10,
        minSize: 0,
      },
      {
        header: 'Actions',
        cell: (props) => <ActionCell row={props.row} />,
        size: 10,
        minSize: 0,
      },
    ],
    []
  );

  const [currentPageStr, setCurrentPage] = useSearchParamsState('page', '1');
  const [activeFilter, setActiveFilter] = useSearchParamsState('active', '1');
  const [searchFilter, setSearchFilter] = useSearchParamsState('search', '');
  const [groupNamesIn, setGroupNameInFilter] = useSearchParamsState('group_names__in', [], true);
  const debouncedSetNameFilter = useCallback(
    debounce((value: string) => {
      setSearchFilter(value);
    }, 1000),
    [setSearchFilter]
  );
  const currentPage = parseInt(currentPageStr);
  const params = {
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
    active: activeFilter === '1',
    search: searchFilter,
    groupNamesIn: groupNamesIn,
  };

  const [searchFilterState, setSearchFilterState] = useState('');

  const queryGroups = useQuery({
    queryKey: groupQueryKeys.list(params as Parameters<typeof GroupService.groupList>[0]),
    queryFn: async () => {
      let groups: Group[] = [];
      const limit = 100;
      let totalCount: number;
      let offset = 0;

      // fetch all facilities considering that data.count is the total number of facilities
      let data = await GroupService.groupList({
        limit,
        offset,
      });
      totalCount = data.count as number;
      groups = groups.concat(data.results || []);
      while (groups.length < totalCount) {
        offset += limit;
        data = await GroupService.groupList({ limit, offset });
        totalCount = data.count as number;
        groups = groups.concat(data.results || []);
      }
      return groups;
    },
    keepPreviousData: true,
  });
  const queryUsers = useQuery({
    queryKey: userQueryKeys.list(params as Parameters<typeof UserService.userList>[0]),
    queryFn: async () => {
      return await UserService.userList(params as Parameters<typeof UserService.userList>[0]);
    },
    keepPreviousData: true,
  });

  const table = useReactTable({
    data: queryUsers?.data?.results ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    debouncedSetNameFilter(searchFilterState);
    return () => debouncedSetNameFilter.cancel();
  }, [searchFilterState, debouncedSetNameFilter]);

  const handleChangePage = (page: number) => {
    setCurrentPage(page.toString());
  };

  const renderItems = () => {
    if (queryUsers.isLoading || queryUsers.isFetching) {
      return <SkeletonTableRows />;
    }

    if (queryUsers.isError) {
      return (
        <TableRow>
          <td colSpan={7} className="text-center">
            Error
          </td>
        </TableRow>
      );
    }

    if (queryUsers.data?.count === 0) {
      return (
        <TableRow>
          <td colSpan={7} className="text-center" />
        </TableRow>
      );
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.id}>
        {row.getVisibleCells().map((cell) => (
          <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
        ))}
      </TableRow>
    ));
  };

  const { control } = useForm({
    defaultValues: {
      group_names__in: groupNamesIn,
    },
  });

  return (
    <>
      <Row className="justify-content-center" xs={'auto'}>
        <Col>
          <Alert variant="danger" show={queryUsers.isError}>
            <Alert.Heading>Something went wrong</Alert.Heading>
            <p>Please try again later. If the problem persists, please contact us.</p>
          </Alert>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col xs={12} className="d-flex gap-3 justify-content-start align-items-center">
          <Form.Control
            type="text"
            placeholder="Search by name or email"
            value={searchFilterState}
            style={{ maxWidth: 400 }}
            onChange={(e) => {
              setSearchFilterState(e.target.value);
              setCurrentPage('1');
            }}
          />
          <SelectController
            control={control}
            name="group_names__in"
            placeholder="Filter by Permissions"
            options={
              queryGroups?.data?.map(({ code }) => ({
                value: code,
                label: GROUP_NAMES_MAPPING[code],
              })) || []
            }
            onChange={(selection: any) => {
              // Extract just the values from the selection array
              const values = Array.isArray(selection)
                ? selection.map((option) => option.value)
                : [];
              setGroupNameInFilter(values);
              setCurrentPage('1');
            }}
          />
          <div className="d-flex align-items-center text-light">
            <span
              className="me-2"
              role="button"
              tabIndex={0}
              onClick={() => setActiveFilter('0')}
              onKeyUp={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  setActiveFilter('0');
                }
              }}
            >
              Inactive
            </span>
            <Form.Check
              type="switch"
              id="custom-switch"
              checked={activeFilter === '1'}
              onChange={(e) => {
                setActiveFilter(e.target.checked ? '1' : '0');
              }}
            />
            <span
              className="me-2"
              role="button"
              tabIndex={0}
              onClick={() => setActiveFilter('1')}
              onKeyUp={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  setActiveFilter('1');
                }
              }}
            >
              Active
            </span>
          </div>
        </Col>
      </Row>

      <Row className="justify-content-center" xs={12}>
        <Col>
          <Card className="mb-4">
            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle mb-0">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          style={{
                            width: `${header.getSize()}%`,
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="table-group-divider">{renderItems()}</tbody>
              </table>
            </div>
          </Card>

          <div className="hstack gap-3 justify-content-center mb-4 text-muted">
            <span>
              {queryUsers.data?.count ?? 0} {queryUsers.data?.count === 1 ? 'user' : 'users'}
            </span>
            <Pagination
              count={queryUsers.data?.count ?? 0}
              pageSize={PAGE_SIZE}
              currentPage={currentPage}
              siblingCount={2}
              onChangePage={handleChangePage}
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

interface TableRowProps {
  children: React.ReactNode;
  'data-testid'?: string;
}

const TableRow = ({ children, ...props }: TableRowProps) => <tr {...props}>{children}</tr>;

const SkeletonTableRows = () => {
  return Array(PAGE_SIZE)
    .fill(0)
    .map((_, index) => (
      <TableRow key={index} data-testid={`skeleton-row-${index}`}>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
      </TableRow>
    ));
};
